import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';
import clx from 'clsx';
import { PhoneNumberUtil } from 'google-libphonenumber';
import Image from 'next/image';
import type { FocusEventHandler, RefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { parseCountry, usePhoneInput } from 'react-international-phone';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useCountrySelector } from '@/hooks/useFormCountryList/useFormCountryList';
import { labelStyles } from '../Input/styles';
import { useOptionalFormContext } from '../hooks/useOptionalFormContext';
import { getErrorMessage } from '../utils/getErrorMessage';
import type { Country } from './countries';
import {
  getCountryByCode,
  getPlaceholderCountry,
  orderCountriesByIso2Priority,
  orderPhoneCountryOptions,
  updatedCountries,
} from './countries';
import { phoneInputStyles, phoneInputWrapperStyles } from './styles';
import type { CountryOption, PhoneInputProps, PhoneInputValue, StringFieldPath } from './types';
import { normalizePhoneNumberForInput } from '@/utils/phone/normalizePhoneNumberForInput';
import { clearPhoneMask } from './utils/clearPhoneMask';
import CheckIcon from '/public/icons/check-tiny.svg';
import ChevronDownIcon from '/public/icons/chevron-down.svg';

const INPUT_HEIGHT = 44;
const FLAG_SIZE = 24;

// Inner component with usePhoneInput — remounted via key when country reset is needed
const PhoneInputCore = <T extends FieldValues, TName extends StringFieldPath<T>>({
  name,
  errors,
  value,
  disabled,
  onChange,
  setError,
  clearErrors,
  onBlur,
  helperText,
  disableDialCodePrefill,
  prioritizedCountries,
  orderedCountries,
}: PhoneInputProps<T, TName> & {
  prioritizedCountries: string[];
  orderedCountries: ReturnType<typeof orderCountriesByIso2Priority>;
}) => {
  const formContext = useOptionalFormContext<T>();
  const errorMessage = getErrorMessage<T>(errors, name);
  const phoneUtil = PhoneNumberUtil.getInstance();

  const phoneInputValue =
    disableDialCodePrefill && !value ? '' : clearPhoneMask(normalizePhoneNumberForInput(value ?? ''));

  const [isCountryLocked, setIsCountryLocked] = useState(false);

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: 'us',
    value: phoneInputValue,
    countries: orderedCountries,
    disableDialCodePrefill,
    disableCountryGuess: isCountryLocked,
    onChange: ({ phone, inputValue, country }) => {
      handleChange({ phoneWithMask: inputValue, phoneWithoutMask: phone, country });
    },
  });

  const handleChange = (values: PhoneInputValue) => {
    if (onChange) {
      onChange(values);
    }
    if (formContext) {
      formContext?.clearErrors(name);
    }
  };

  const isPhoneValid = (phone: string) => {
    try {
      return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
    } catch {
      return false;
    }
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
    onBlur?.(event);
    const trimmed = inputValue?.trim() ?? '';
    if (trimmed === '') {
      clearErrors?.(name);
      return;
    }
    if (!isPhoneValid(inputValue)) {
      setError?.(name, { message: 'Invalid phone number' });
      return;
    }
    clearErrors?.(name);
  };

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputWrapRef = useRef<HTMLDivElement>(null);
  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
      offset(8),
      flip({ rootBoundary: 'document', altBoundary: true }),
      shift({ rootBoundary: 'document', altBoundary: true }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useClickOutside(
    [
      dropdownRef as RefObject<HTMLElement>,
      inputWrapRef as RefObject<HTMLElement>,
      refs.floating as RefObject<HTMLElement>,
    ],
    () => setDropdownOpen(false)
  );

  const countryOptions = useMemo<CountryOption[]>(
    () =>
      orderPhoneCountryOptions(updatedCountries, prioritizedCountries, country.iso2).map((countryData) =>
        parseCountry(countryData)
      ),
    [prioritizedCountries, country.iso2]
  );

  const onOpenDropdown = () => {
    if (disabled) return;
    setDropdownOpen((open) => !open);
  };

  return (
    <>
      <div
        className={phoneInputWrapperStyles({ error: Boolean(errorMessage), disabled: Boolean(disabled) })}
        ref={refs.setReference}
        style={{ minHeight: INPUT_HEIGHT, paddingLeft: 0, paddingRight: 0 }}
      >
        <button
          type="button"
          aria-label={country.name}
          aria-haspopup="listbox"
          aria-expanded={dropdownOpen}
          disabled={disabled}
          onClick={onOpenDropdown}
          className={clx(
            'flex items-center px-3 py-0 min-w-[70px] h-full select-none border-none bg-transparent relative',
            'hover:bg-blue-50 disabled:hover:bg-transparent disabled:cursor-not-allowed rounded-l-[6px]'
          )}
          tabIndex={0}
          style={{ height: INPUT_HEIGHT }}
        >
          <span style={{ fontSize: FLAG_SIZE }}>
            <CountryFlag country={getCountryByCode(country.iso2)} />
          </span>
          <span className="ml-[2px] flex items-center">
            <ChevronDownIcon
              className={clx(
                dropdownOpen ? 'rotate-180' : '',
                'transition-transform duration-200',
                'text-neutral-700 w-3 h-3 fill-current'
              )}
            />
          </span>
        </button>
        <input
          name={name}
          ref={inputRef}
          placeholder={getPlaceholderCountry(country.iso2)?.placeholder || 'Enter your phone number'}
          type="tel"
          value={inputValue}
          disabled={disabled}
          onChange={handlePhoneValueChange}
          onBlur={handleBlur}
          className={phoneInputStyles()}
          style={{ height: INPUT_HEIGHT }}
        />
      </div>
      {dropdownOpen && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className="bg-white z-50 rounded-[6px] shadow-card overflow-y-auto max-h-64"
          role="listbox"
          tabIndex={-1}
        >
          <ul className="max-h-52 overflow-y-auto px-[6px] py-1">
            {countryOptions.map((c) => (
              <li
                key={c.iso2}
                className={clx('flex items-center px-3 py-2 cursor-pointer rounded-[2px] h-[47px]', {
                  'bg-blue-50': country.iso2 === c.iso2,
                  'hover:bg-blue-50': country.iso2 !== c.iso2,
                })}
                role="option"
                aria-selected={country.iso2 === c.iso2}
                tabIndex={-1}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setCountry(c.iso2);
                  setIsCountryLocked(true);
                  setDropdownOpen(false);
                }}
              >
                <div className="flex items-center">
                  <CountryFlag country={getCountryByCode(c.iso2)} />
                  <span className="text-base text-black  leading-[150%] font-inter">{c.name}</span>
                  <span className="text-base text-grey-700 leading-[150%] font-inter ml-2">(+{c.dialCode})</span>
                </div>
                {country.iso2 === c.iso2 && <CheckIcon className="w-4 h-4 ml-auto text-black" />}
              </li>
            ))}
          </ul>
        </div>
      )}
      {errorMessage && <p className="text-red-500 text-sm mt-[4px] text-left font font-inter">{errorMessage}</p>}
      {helperText && <p className="text-blue-300 text-body-small mt-[4px]">{helperText}</p>}
    </>
  );
};

// Outer wrapper — manages resetKey to remount PhoneInputCore when country needs to reset to US
export const PhoneInput = <T extends FieldValues, TName extends StringFieldPath<T>>(
  props: PhoneInputProps<T, TName>
) => {
  const { value, disableDialCodePrefill, className, label, labelStyle, required, name } = props;
  const { prioritizedCountries } = useCountrySelector({ url: props.countriesUrl ?? '/api/worldwide/countries' });
  const orderedCountries = useMemo(
    () => orderCountriesByIso2Priority(updatedCountries, prioritizedCountries),
    [prioritizedCountries]
  );

  const prevValueRef = useRef(value);
  const [resetKey, setResetKey] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const prevValue = prevValueRef.current;
    prevValueRef.current = value;
    if (disableDialCodePrefill && !value && prevValue) {
      setResetKey((k) => k + 1);
    }
  }, [value, disableDialCodePrefill]);

  return (
    <div className={className} ref={wrapperRef} style={{ position: 'relative' }}>
      {label && (
        <label
          className={clx(labelStyles({ intent: labelStyle?.intent || 'primary', required }), 'text-sm')}
          htmlFor={name}
        >
          {label}
        </label>
      )}
      <PhoneInputCore
        key={resetKey}
        {...props}
        prioritizedCountries={prioritizedCountries}
        orderedCountries={orderedCountries}
      />
    </div>
  );
};

const CountryFlag = ({ country }: { country?: Country }) => {
  if (country?.flagSvg) {
    return (
      <div className="flex items-center justify-center w-6 h-4 mr-2 flex-shrink-0">
        <Image
          src={country.flagSvg}
          alt={`${country.name} flag`}
          width={24}
          height={16}
          className="rounded-[2px] object-cover w-full h-full"
          unoptimized
        />
      </div>
    );
  }

  return <span className="text-lg mr-2 flex-shrink-0">{country?.iso2.toUpperCase() || ''}</span>;
};